import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../redux/slices/authSlice';

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const fetchingStatesRef = useRef(false);
  const initialCountrySetRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    if (user) {
      setFormData({
        firstName: user.User_First_Name || user.user_First_Name || user.firstName || '',
        lastName: user.User_Last_Name || user.user_Last_Name || user.lastName || '',
        phone: user.User_Phone || user.user_Phone || user.phone || '',
        email: user.User_Email || user.user_Email || user.email || '',
        addressLine1: user.User_Address_Line1 || user.user_Address_Line1 || user.Address_Line1 || user.address_Line1 || user.addressLine1 || '',
        addressLine2: user.User_Address_Line2 || user.user_Address_Line2 || user.Address_Line2 || user.address_Line2 || user.addressLine2 || '',
        city: user.User_City || user.user_City || user.City || user.city || '',
        state: user.User_State || user.user_State || user.State || user.state || '',
        zipcode: user.User_Zipcode || user.user_Zipcode || user.ZipCode || user.zipCode || user.zipcode || '',
        country: user.User_Country || user.user_Country || user.Country || user.country || '',
      });
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch countries on mount
  useEffect(() => { fetchCountries(); }, []);

  // Once countries are loaded, map user's country name to its ID
  useEffect(() => {
    if (countries.length > 0 && user && !initialCountrySetRef.current) {
      const userCountryName = (user.User_Country || user.user_Country || user.Country || user.country || '').toLowerCase().trim();
      if (userCountryName) {
        const found = countries.find(c =>
          (c.country_Name || c.countryName || '').toLowerCase().trim() === userCountryName
        );
        if (found) {
          initialCountrySetRef.current = true;
          setFormData(prev => ({ ...prev, country: String(found.country_Id || found.countryId) }));
        }
      }
    }
  }, [countries, user]);

  // Fetch states whenever country changes
  useEffect(() => {
    if (formData.country && !fetchingStatesRef.current) {
      fetchStatesByCountry(formData.country);
    } else if (!formData.country) {
      setStates([]);
    }
  }, [formData.country]);

  const fetchCountries = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      const res = await fetch(`${apiBaseUrl}/CommonChannel/GetCountries`);
      if (res.ok) setCountries(await res.json() || []);
    } catch (err) { console.error('Error fetching countries:', err); }
  };

  const fetchStatesByCountry = async (countryId) => {
    if (fetchingStatesRef.current) return;
    fetchingStatesRef.current = true;
    try {
      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      const res = await fetch(`${apiBaseUrl}/CommonChannel/GetStatesByCountry?countryId=${countryId}`);
      if (res.ok) setStates(await res.json() || []);
      else setStates([]);
    } catch (err) { console.error('Error fetching states:', err); setStates([]); }
    finally { fetchingStatesRef.current = false; }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitized = value;
    if (name === 'phone') sanitized = value.replace(/\D/g, '').slice(0, 20);
    if (name === 'zipcode') sanitized = value.replace(/[^A-Za-z0-9 \-]/g, '').slice(0, 10);
    if (name === 'country') {
      setFormData(prev => ({ ...prev, country: sanitized, state: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: sanitized }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = 'First name is required';
    else if (formData.firstName.trim().length > 30) errs.firstName = 'Max 30 characters';
    if (!formData.lastName.trim()) errs.lastName = 'Last name is required';
    else if (formData.lastName.trim().length > 30) errs.lastName = 'Max 30 characters';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email)) errs.email = 'Enter a valid email address';
    if (formData.phone) {
      const digits = formData.phone.replace(/\D/g, '');
      if (digits.length !== 10) errs.phone = 'Phone must be exactly 10 digits';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      const userId = user?.User_Id || user?.user_Id || user?.userId;
      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      const selectedCountry = countries.find(c => String(c.country_Id || c.countryId) === String(formData.country));
      const countryName = selectedCountry ? (selectedCountry.country_Name || selectedCountry.countryName) : formData.country;
      const response = await fetch(`${apiBaseUrl}/CommonChannel/UpdateUser`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          User_Id: userId,
          User_First_Name: formData.firstName.trim(),
          User_Last_Name: formData.lastName.trim(),
          User_Phone: formData.phone,
          User_Email: formData.email.trim(),
          User_Address_Line1: formData.addressLine1.trim(),
          User_Address_Line2: formData.addressLine2.trim(),
          User_City: formData.city.trim(),
          User_State: formData.state.trim(),
          User_Zipcode: formData.zipcode,
          User_Country: countryName,
          ModifiedBy: formData.email.trim(),
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to update profile');
      }

      dispatch(updateUser({
        User_First_Name: formData.firstName.trim(),
        user_First_Name: formData.firstName.trim(),
        User_Last_Name: formData.lastName.trim(),
        user_Last_Name: formData.lastName.trim(),
        User_Phone: formData.phone,
        User_Email: formData.email.trim(),
        email: formData.email.trim(),
        User_Address_Line1: formData.addressLine1.trim(),
        User_Address_Line2: formData.addressLine2.trim(),
        User_City: formData.city.trim(),
        User_State: formData.state.trim(),
        User_Zipcode: formData.zipcode,
        User_Country: countryName,
        Address_Line1: formData.addressLine1.trim(),
        Address_Line2: formData.addressLine2.trim(),
        City: formData.city.trim(),
        State: formData.state.trim(),
        ZipCode: formData.zipcode,
        Country: countryName,
      }));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setApiError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-100'
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-800 font-semibold py-2 px-4 rounded-full transition shadow-sm hover:shadow-md flex items-center gap-2 text-sm"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-700">Edit Profile</h1>
            <p className="text-sm text-gray-500">Update your personal details</p>
          </div>
        </div>

        {/* Success Banner */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-green-500 text-xl">✅</span>
            <p className="text-green-800 font-medium">Profile updated successfully!</p>
          </div>
        )}

        {/* Error Banner */}
        {apiError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-red-500 text-xl">⚠️</span>
            <p className="text-red-800">{apiError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-6">

          {/* Name */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  maxLength={30}
                  className={inputClass('firstName')}
                  placeholder="First name"
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-0.5">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  maxLength={30}
                  className={inputClass('lastName')}
                  placeholder="Last name"
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-0.5">{errors.lastName}</p>}
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
              Contact Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={100}
                  className={inputClass('email')}
                  placeholder="email@example.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={20}
                  className={inputClass('phone')}
                  placeholder="10-digit phone number"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
              Address
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  maxLength={100}
                  className={inputClass('addressLine1')}
                  placeholder="Street address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  maxLength={100}
                  className={inputClass('addressLine2')}
                  placeholder="Apt, Suite, Unit (optional)"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={inputClass('country')}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.country_Id || c.countryId} value={String(c.country_Id || c.countryId)}>
                        {c.country_Name || c.countryName}
                      </option>
                    ))}
                  </select>
                  {errors.country && <p className="text-red-500 text-xs mt-0.5">{errors.country}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    disabled={!formData.country || states.length === 0}
                    className={`${inputClass('state')} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  >
                    <option value="">Select State</option>
                    {states.map((s, i) => {
                      const sName = (s.stateName || s.state_Name || '').trim();
                      return <option key={i} value={sName}>{sName}</option>;
                    })}
                  </select>
                  {errors.state && <p className="text-red-500 text-xs mt-0.5">{errors.state}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    maxLength={50}
                    className={inputClass('city')}
                    placeholder="City"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {(() => {
                      const sc = countries.find(c => String(c.country_Id || c.countryId) === String(formData.country));
                      const cn = (sc?.country_Name || sc?.countryName || '').toLowerCase();
                      if (cn.includes('canada')) return 'Postal Code';
                      if (cn.includes('united states') || cn.includes('usa')) return 'Zip Code';
                      return 'Zip / Postal Code';
                    })()}
                  </label>
                  <input
                    type="text"
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    maxLength={10}
                    className={inputClass('zipcode')}
                    placeholder={(() => {
                      const sc = countries.find(c => String(c.country_Id || c.countryId) === String(formData.country));
                      const cn = (sc?.country_Name || sc?.countryName || '').toLowerCase();
                      if (cn.includes('canada')) return 'L8G 3X4';
                      if (cn.includes('united states') || cn.includes('usa')) return '12345';
                      return 'Postal / Zip code';
                    })()}
                  />
                  {errors.zipcode && <p className="text-red-500 text-xs mt-0.5">{errors.zipcode}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-5 rounded-full border-2 border-gray-200 transition shadow-sm hover:shadow-md text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-5 rounded-full transition-all duration-300 shadow-md hover:shadow-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
